import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import {
  Shield, Search, Copy, CheckCircle2, Loader2, BookOpen, Zap, FileText,
  Filter, Globe, MessageSquare, ArrowRight, Star, ThumbsUp, ThumbsDown,
  Pen, ChevronDown, ChevronUp, X
} from "lucide-react";

const SEVERITY_STYLES = {
  HIGH: "bg-red-500/20 text-red-400 border-red-500/30",
  MEDIUM: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  LOW: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
};

const DOMAIN_LABELS = {
  medical_device: "Medical Device", pharma: "Pharma / GxP", pharma_medical: "Pharma + Medical",
  combination_product: "Combo Product", industrial_electrical: "Industrial / IEC",
  electronics: "Electronics", software: "Software / SaMD", cybersecurity: "Cybersecurity",
  laboratory: "Laboratory", automotive: "Automotive",
};

const DefenseScriptPage = () => {
  const { t } = useTranslation('audit');
  const [scenarios, setScenarios] = useState([]);
  const [allDomains, setAllDomains] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [personalizedResult, setPersonalizedResult] = useState(null);
  const [personalizing, setPersonalizing] = useState(false);
  const [instantiating, setInstantiating] = useState(false);
  const [category, setCategory] = useState("All");
  const [domain, setDomain] = useState("All");
  const [roomType, setRoomType] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [contextValues, setContextValues] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState({});
  const [instances, setInstances] = useState([]);

  const fetchScenarios = useCallback(async () => {
    setLoading(true);
    try {
      const params = [];
      if (category !== "All") params.push(`category=${encodeURIComponent(category)}`);
      if (domain !== "All") params.push(`domain=${encodeURIComponent(domain)}`);
      if (roomType !== "All") params.push(`room_type=${encodeURIComponent(roomType)}`);
      if (searchQuery) params.push(`q=${encodeURIComponent(searchQuery)}`);
      const url = `${API}/auditiq/defense/scenarios${params.length ? "?" + params.join("&") : ""}`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setScenarios(d.scenarios || []);
        if (d.domains) setAllDomains(d.domains);
        if (d.categories) setAllCategories(d.categories);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [category, domain, roomType, searchQuery]);

  useEffect(() => { fetchScenarios(); }, [fetchScenarios]);

  const personalize = async () => {
    if (!selected || personalizing) return;
    setPersonalizing(true);
    try {
      const res = await fetch(`${API}/auditiq/defense/personalize`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scenario_id: selected.id, context: contextValues }),
      });
      if (res.ok) setPersonalizedResult(await res.json());
    } catch (e) { console.error(e); }
    setPersonalizing(false);
  };

  const instantiate = async (auditId = "", checklistItemId = "", findingId = "") => {
    if (!selected || instantiating) return;
    setInstantiating(true);
    try {
      const res = await fetch(`${API}/auditiq/defense/instantiate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template_id: selected.id,
          audit_id: auditId || undefined,
          checklist_item_id: checklistItemId || undefined,
          finding_id: findingId || undefined,
          generated_for: "manual_library",
          personalized_content: personalizedResult || undefined,
        }),
      });
      if (res.ok) {
        const d = await res.json();
        setInstances(prev => [d.instance, ...prev]);
        alert(`Defense script instantiated: ${d.instance.instance_id}`);
      }
    } catch (e) { console.error(e); }
    setInstantiating(false);
  };

  const sendFeedback = async (targetId, rating) => {
    try {
      await fetch(`${API}/auditiq/defense/feedback`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_id: targetId, rating }),
      });
      setFeedbackSent(p => ({ ...p, [targetId]: rating }));
    } catch (e) { console.error(e); }
  };

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <QMSLayout title={t('audit:defenseScript.title')}>
      <div data-testid="defense-script-page" className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>{t('defenseScript.title')}</h1>
              <p className="text-xs text-[#94A3B8]">{t('defenseScript.subtitle', { count: scenarios.length, domains: allDomains.length })}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: "calc(100vh - 200px)" }}>
          {/* Left: Filters + List */}
          <div className="lg:col-span-5 space-y-3">
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-3 space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-[#94A3B8]" />
                <input data-testid="defense-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder={t('defenseScript.search')}
                  className="w-full pl-8 pr-3 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded-lg text-xs text-white placeholder-[#94A3B8]/50 focus:border-[#1A6BCC] focus:outline-none" />
              </div>
              <div className="flex items-center gap-1.5">
                <Globe className="w-3 h-3 text-[#94A3B8] flex-shrink-0" />
                <div className="flex flex-wrap gap-1">
                  <button onClick={() => setDomain("All")} className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${domain === "All" ? "bg-[#1A6BCC] text-white" : "bg-[#132340] text-[#94A3B8] hover:text-white"}`}>All</button>
                  {allDomains.map(d => (
                    <button key={d} onClick={() => setDomain(d)} data-testid={`domain-filter-${d}`}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${domain === d ? "bg-[#1A6BCC] text-white" : "bg-[#132340] text-[#94A3B8] hover:text-white"}`}>
                      {DOMAIN_LABELS[d] || d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Filter className="w-3 h-3 text-[#94A3B8] flex-shrink-0" />
                <div className="flex flex-wrap gap-1">
                  <button onClick={() => setCategory("All")} className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${category === "All" ? "bg-cyan-500/30 text-cyan-300" : "bg-[#132340] text-[#94A3B8] hover:text-white"}`}>All</button>
                  {allCategories.map(c => (
                    <button key={c} onClick={() => setCategory(c)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${category === c ? "bg-cyan-500/30 text-cyan-300" : "bg-[#132340] text-[#94A3B8] hover:text-white"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <MessageSquare className="w-3 h-3 text-[#94A3B8] flex-shrink-0" />
                <div className="flex gap-1">
                  {["All", "front_room", "back_room", "both"].map(rt => (
                    <button key={rt} onClick={() => setRoomType(rt)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${roomType === rt ? "bg-purple-500/30 text-purple-300" : "bg-[#132340] text-[#94A3B8] hover:text-white"}`}>
                      {rt === "All" ? "All Rooms" : rt.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl overflow-hidden max-h-[calc(100vh-380px)] overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center"><Loader2 className="w-5 h-5 text-[#94A3B8] animate-spin mx-auto" /></div>
              ) : scenarios.length === 0 ? (
                <div className="p-6 text-center text-[#94A3B8] text-sm">{t('defenseScript.noScenarios')}</div>
              ) : (
                scenarios.map(s => (
                  <button key={s.id} onClick={() => { setSelected(s); setPersonalizedResult(null); setContextValues({}); setShowDrawer(true); }}
                    data-testid={`defense-scenario-${s.id}`}
                    className={`w-full text-left p-3 border-b border-[#1E3A5F] transition-colors ${selected?.id === s.id ? "bg-[#1A6BCC]/15" : "hover:bg-[#132340]"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                          <span className="text-[10px] font-mono text-[#94A3B8]">{s.id}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${SEVERITY_STYLES[s.severity]}`}>{s.severity}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-[#132340] text-cyan-400">{DOMAIN_LABELS[s.domain] || s.domain}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-500/10 text-purple-300 capitalize">{(s.room_type || "both").replace("_", " ")}</span>
                        </div>
                        <p className="text-xs text-white font-medium">{s.scenario}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-[#94A3B8]">{s.standard}</span>
                          <span className="text-[10px] text-[#1A6BCC]">{s.key_defense_term}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right: Detail Drawer */}
          <div className="lg:col-span-7 space-y-3">
            {!selected ? (
              <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-12 text-center">
                <Shield className="w-16 h-16 text-[#1E3A5F] mx-auto mb-4" />
                <h3 className="text-lg text-[#94A3B8] font-medium mb-1">{t('defenseScript.selectScenario')}</h3>
                <p className="text-sm text-[#94A3B8]/60">{t('defenseScript.selectScenarioDesc', { count: scenarios.length })}</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-3" data-testid="defense-detail-drawer">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-[#00C8E0] uppercase tracking-wider">{selected.id}</h3>
                      <span className="px-2 py-0.5 rounded text-[10px] bg-cyan-500/20 text-cyan-300">{DOMAIN_LABELS[selected.domain] || selected.domain}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${SEVERITY_STYLES[selected.severity]}`}>{selected.severity}</span>
                      <button onClick={() => { setSelected(null); setShowDrawer(false); }} className="text-[#94A3B8] hover:text-white"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <h2 className="text-base text-white font-medium">{selected.scenario}</h2>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 rounded bg-[#132340] text-[#94A3B8]"><BookOpen className="w-3 h-3 inline mr-1" />{selected.standard}</span>
                    <span className="px-2 py-1 rounded bg-[#1A6BCC]/20 text-[#1A6BCC]"><Zap className="w-3 h-3 inline mr-1" />{selected.key_defense_term}</span>
                    {selected.criticality_tags?.map(item => <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-red-500/10 text-red-400">{t}</span>)}
                  </div>

                  {/* Likely Auditor Question */}
                  <div className="p-2.5 bg-amber-500/5 border border-amber-500/20 rounded">
                    <span className="text-[10px] font-bold text-amber-400 uppercase">{t('defenseScript.likelyAuditorQuestion')}</span>
                    <p className="text-xs text-[#94A3B8] mt-0.5">{selected.auditor_question}</p>
                  </div>

                  {/* Recommended Response */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">{t('defenseScript.recommendedResponse')}</span>
                      <button onClick={() => copyText(selected.recommended_response, "response")} className="text-[#94A3B8] hover:text-white text-xs flex items-center gap-1">
                        {copiedId === "response" ? <><CheckCircle2 className="w-3 h-3 text-emerald-400" /> {t('defenseScript.copied')}</> : <><Copy className="w-3 h-3" /> {t('defenseScript.copy')}</>}
                      </button>
                    </div>
                    <p className="text-sm text-white leading-relaxed bg-[#132340] p-3 rounded-lg">{selected.recommended_response}</p>
                  </div>

                  {/* Collapsible sections */}
                  <CollapsibleSection title={t('defenseScript.frontRoomTalkingPoints')} items={selected.front_room_talking_points} icon={<MessageSquare className="w-3 h-3 text-cyan-400" />} />
                  <CollapsibleSection title={t('defenseScript.backRoomRetrievalChecklist')} items={selected.back_room_retrieval_checklist} icon={<FileText className="w-3 h-3 text-purple-400" />} />
                  <CollapsibleSection title={t('defenseScript.expectedEvidence')} items={selected.expected_evidence} icon={<BookOpen className="w-3 h-3 text-emerald-400" />} />
                  <CollapsibleSection title={t('defenseScript.commonFailureModes')} items={selected.common_failure_modes} icon={<Shield className="w-3 h-3 text-red-400" />} />
                  <CollapsibleSection title={t('defenseScript.followUpQuestions')} items={selected.follow_up_questions} icon={<Search className="w-3 h-3 text-amber-400" />} />

                  {/* Escalation Note */}
                  <div className="p-2.5 bg-red-500/5 border border-red-500/20 rounded">
                    <span className="text-[10px] font-bold text-red-400 uppercase">{t('defenseScript.escalationNote')}</span>
                    <p className="text-xs text-[#94A3B8] mt-0.5">{selected.escalation_note}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t border-[#1E3A5F]">
                    <button onClick={() => instantiate()} disabled={instantiating} data-testid="instantiate-script-btn"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-black text-xs font-bold rounded-[3px] disabled:opacity-50">
                      {instantiating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                      {t('defenseScript.instantiate')}
                    </button>
                    <button onClick={() => copyText(selected.defense_template, "template")} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#132340] text-[#94A3B8] hover:text-white text-xs rounded-[3px]">
                      <Copy className="w-3.5 h-3.5" /> {t('defenseScript.copyTemplate')}
                    </button>
                    {/* Feedback */}
                    {feedbackSent[selected.id] ? (
                      <span className="text-xs text-emerald-400 ml-auto">{t('defenseScript.feedbackSent')}</span>
                    ) : (
                      <div className="flex items-center gap-1 ml-auto">
                        <button onClick={() => sendFeedback(selected.id, "useful")} className="p-1 text-[#94A3B8] hover:text-emerald-400" title="Useful"><ThumbsUp className="w-3.5 h-3.5" /></button>
                        <button onClick={() => sendFeedback(selected.id, "not_useful")} className="p-1 text-[#94A3B8] hover:text-red-400" title="Not useful"><ThumbsDown className="w-3.5 h-3.5" /></button>
                        <button onClick={() => sendFeedback(selected.id, "needs_revision")} className="p-1 text-[#94A3B8] hover:text-amber-400" title="Needs revision"><Pen className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Personalization */}
                <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-3">
                  <h3 className="text-sm font-bold text-amber-400 uppercase tracking-wider">{t('defenseScript.personalization.title')}</h3>
                  <p className="text-xs text-[#94A3B8]">{t('defenseScript.personalization.desc')}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {["value", "n", "sigma", "cpk", "protocol_ref", "root_cause"].map(field => (
                      <div key={field}>
                        <label className="text-[10px] text-[#94A3B8] mb-0.5 block capitalize">{field.replace("_", " ")}</label>
                        <input value={contextValues[field] || ""} onChange={e => setContextValues(p => ({ ...p, [field]: e.target.value }))}
                          placeholder={field} className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white placeholder-[#94A3B8]/30" />
                      </div>
                    ))}
                  </div>
                  <button data-testid="personalize-script-btn" onClick={personalize} disabled={personalizing}
                    className="w-full py-2 rounded-lg bg-amber-500 text-black font-bold text-sm hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {personalizing ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('defenseScript.personalization.personalizing')}</> : <><Zap className="w-4 h-4" /> {t('defenseScript.personalization.personalizeScript')}</>}
                  </button>
                </div>

                {personalizedResult && (
                  <div className="bg-[#0F1E35] border border-emerald-500/30 rounded-xl p-4 space-y-3" data-testid="personalized-result">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-wider">Personalized Defense (Review Required)</h3>
                      <button onClick={() => copyText(personalizedResult.personalized_script, "personalized")} className="text-[#94A3B8] hover:text-white text-xs flex items-center gap-1">
                        {copiedId === "personalized" ? <><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    </div>
                    <p className="text-sm text-white leading-relaxed bg-[#132340] p-3 rounded-lg border border-emerald-500/20">{personalizedResult.personalized_script}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

const CollapsibleSection = ({ title, items, icon }) => {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  return (
    <div className="border border-[#1E3A5F] rounded">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-2 hover:bg-[#132340]/50">
        <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider flex items-center gap-1.5">{icon} {title} ({items.length})</span>
        {open ? <ChevronUp className="w-3 h-3 text-[#94A3B8]" /> : <ChevronDown className="w-3 h-3 text-[#94A3B8]" />}
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-0.5">
          {items.map((item, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-[#1A6BCC] mt-0.5 flex-shrink-0" />
              <span className="text-xs text-[#94A3B8]">{item}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DefenseScriptPage;
